import { AppHTTPService } from '@/api/http-service'
import { Button, Icon } from '@flatfile/baseline'
import { ProjectThumbnail } from '@/components/ProjectThumbnail/ProjectThumbnail'
import { useQuery } from '@tanstack/react-query'
import { formatDistanceToNow } from 'date-fns'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/auth/AuthProvider'

interface Project {
  id: string
  name: string
  createdAt?: string
  updatedAt?: string
  filesCount?: number
}

export default function ProjectsGridView() {
  const navigate = useNavigate()
  const { isAuthenticated, isLoading: authLoading } = useAuth()

  // You can change this to try different thumbnail variants
  const thumbnailVariant: 'grid' | 'pattern' | 'circular' | 'heatmap' | 'wave' =
    'wave'

  const {
    data: projects,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      const response = await AppHTTPService.client.projects.list()
      return response as Project[]
    },
    enabled: isAuthenticated && !authLoading, // Only run when authenticated and not loading
  })

  const handleProjectClick = (projectId: string) => {
    navigate(`/projects/${projectId}/prepare-chat`)
  }

  const handleCreateProject = async () => {
    const newProject =
      await AppHTTPService.client.projects.create('New Project')
    if (newProject?.id) {
      navigate(`/projects/${newProject.id}/prepare-chat`)
    }
  }

  if (isLoading) {
    return (
      <div className='flex items-center justify-center h-screen'>
        <div className='text-gray-500'>Loading projects...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className='flex items-center justify-center h-screen'>
        <div className='text-red-500'>Error loading projects</div>
      </div>
    )
  }

  return (
    <div className='min-h-screen bg-white'>
      {/* Page Header */}
      <div className='border-b border-gray-200 bg-white'>
        <div className='px-8 py-6'>
          <h1 className='text-2xl font-semibold text-gray-900'>All Projects</h1>
          <p className='mt-1 text-sm text-gray-500'>
            View and manage all your data preparation projects
          </p>
        </div>
      </div>

      {/* Grid Content */}
      <div className='px-8 py-8'>
        {projects?.length === 0 ? (
          <div className='text-center py-12'>
            <Icon
              name='Folder'
              size={32}
              className='mx-auto mb-4 text-gray-400'
            />
            <h3 className='text-lg font-medium text-gray-900 mb-2'>
              No projects yet
            </h3>
            <p className='text-gray-500 mb-4'>
              Get started by creating your first project
            </p>
            <Button
              onClick={handleCreateProject}
              variant='primary'
              size='md'
              icon='Plus'
            >
              Create Project
            </Button>
          </div>
        ) : (
          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'>
            {projects?.map((project) => (
              <div
                key={project.id}
                onClick={() => handleProjectClick(project.id)}
                className='bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow cursor-pointer group overflow-hidden'
              >
                {/* Thumbnail Section */}
                <div className='relative'>
                  <ProjectThumbnail
                    projectId={project.id}
                    variant={thumbnailVariant}
                  />
                  <div className='absolute top-2 right-2'>
                    <Icon
                      name='ChevronRight'
                      size={16}
                      className='text-gray-400 group-hover:text-gray-600 transition-colors'
                    />
                  </div>
                </div>

                {/* Content Section */}
                <div className='p-4 space-y-2'>
                  <h3 className='text-lg font-medium text-gray-900 truncate'>
                    {project.name || 'Untitled Project'}
                  </h3>

                  {project.updatedAt && (
                    <p className='text-sm text-gray-500'>
                      Updated{' '}
                      {formatDistanceToNow(new Date(project.updatedAt), {
                        addSuffix: true,
                      })}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
